import { useTranslation } from 'react-i18next';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * A percentage change in basis points, against the previous equivalent period.
 *
 * Null means the previous value was zero, so no percentage exists: it reads "N/A", never infinity.
 * `invert` is for figures where a rise is bad, such as expenses.
 */
export function ChangeText({
  basisPoints,
  invert = false,
}: {
  basisPoints: number | null | undefined;
  invert?: boolean;
}) {
  const { t, i18n } = useTranslation();

  if (basisPoints === undefined) return null;
  if (basisPoints === null) {
    return <span className="text-muted text-xs">{t('reports.change.notAvailable')}</span>;
  }

  const up = basisPoints > 0;
  const flat = basisPoints === 0;
  const good = flat ? null : invert ? !up : up;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const percent = new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(basisPoints / 100);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        good === null ? 'text-muted' : good ? 'text-emerald-700' : 'text-red-700',
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      <span dir="ltr">{percent}%</span>
      <span className="text-muted font-normal">{t('reports.change.vsPrevious')}</span>
    </span>
  );
}
